import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, Mail, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { QUESTIONS, PROFILES, computeProfile } from "@/data/quiz";
import { submitLead } from "@/lib/api";

const AMAZON_URL = "https://www.amazon.com/dp/B0C5RZF56D";
const APP_URL = "https://app.marrsinglife.com";

export default function Quiz() {
  const [stage, setStage] = useState("hero"); // hero | quiz | gate | result
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [relationshipStage, setRelationshipStage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const result = useMemo(() => {
    if (answers.length < QUESTIONS.length) return null;
    return computeProfile(answers);
  }, [answers]);

  const profile = result ? PROFILES[result.key] : null;

  function selectAnswer(idx) {
    const next = [...answers];
    next[current] = idx;
    setAnswers(next);
  }
  function goNext() {
    if (answers[current] === undefined) return;
    if (current < QUESTIONS.length - 1) {
      setCurrent(current + 1);
      window.scrollTo({ top: document.getElementById("quiz-card")?.offsetTop - 80, behavior: "smooth" });
    } else {
      setStage("gate");
      window.scrollTo({ top: document.getElementById("quiz-card")?.offsetTop - 80, behavior: "smooth" });
    }
  }
  function goBack() {
    if (current > 0) setCurrent(current - 1);
  }

  async function submit() {
    if (!name.trim()) {
      toast.error("Please enter your first name.");
      return;
    }
    if (!email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!relationshipStage) {
      toast.error("Please select your relationship stage.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        name: name || null,
        email,
        source: "quiz",
        profile: result.key,
        scores: result.scores,
        answers,
        extra: {
          first_name: name,
          relationship_stage: relationshipStage,
        },
      });
      toast.success("Your results are unlocked.");
      setStage("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Could not save right now. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", overflowX: "hidden" }}>
      <Nav variant="light" />

      <QuizHero onStart={() => { setStage("quiz"); setTimeout(() => { document.getElementById("quiz-card")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50); }} />

      <WelcomeNote />

      <Pillars />

      <section id="quiz-card" data-testid="quiz-card-section" style={{
        padding: "72px 24px 120px",
        background: `
          radial-gradient(circle at 20% 0%, rgba(111,199,242,0.14), transparent 36%),
          linear-gradient(180deg, var(--mc-cream) 0%, var(--mc-light) 100%)
        `,
      }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
              <span style={{ width: 32, height: 1, background: "var(--mc-blue)" }} />
              <span className="mc-eyebrow">The MarrsingClause® Clarity Quiz</span>
              <span style={{ width: 32, height: 1, background: "var(--mc-blue)" }} />
            </div>
            <h2 className="mc-h2" style={{ fontSize: "clamp(30px, 4vw, 46px)" }}>
              8 questions. <em>One honest look.</em>
            </h2>
            <p style={{ fontSize: 14.5, color: "var(--mc-text-3)", marginTop: 10 }}>
              Answer honestly. There are no right answers — only true ones.
            </p>
          </div>

          <div style={{
            background: "rgba(255,255,255,0.96)",
            border: "1px solid var(--mc-border-blue)",
            borderRadius: 28,
            overflow: "hidden",
            boxShadow: "0 18px 50px rgba(14,10,31,0.08)",
          }}>
            <Progress current={current} total={QUESTIONS.length} stage={stage} />

            {stage === "quiz" && (
              <QuizStep
                question={QUESTIONS[current]}
                index={current}
                total={QUESTIONS.length}
                selected={answers[current]}
                onSelect={selectAnswer}
                onNext={goNext}
                onBack={goBack}
              />
            )}

            {stage === "hero" && (
              <QuizIntro onStart={() => setStage("quiz")} />
            )}

            {stage === "gate" && profile && (
              <EmailGate
                profile={profile}
                name={name} setName={setName}
                email={email} setEmail={setEmail}
                relationshipStage={relationshipStage} setRelationshipStage={setRelationshipStage}
                submitting={submitting}
                onSubmit={submit}
                onBack={() => { setStage("quiz"); setCurrent(QUESTIONS.length - 1); }}
              />
            )}

            {stage === "result" && profile && result && (
              <ResultView profile={profile} scores={result.scores} />
            )}
          </div>
        </div>
      </section>

      <SocialProof />
      <Footer />
    </div>
  );
}

/* ────────────────────────────── HERO ────────────────────────────── */
function QuizHero({ onStart }) {
  return (
    <section data-testid="quiz-hero" style={{
      position: "relative",
      minHeight: "82vh",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "120px 24px 72px", textAlign: "center", overflow: "hidden",
      background: `
        radial-gradient(circle at 80% 12%, rgba(111,199,242,0.32), transparent 38%),
        radial-gradient(circle at 12% 78%, rgba(215,181,109,0.18), transparent 42%),
        linear-gradient(180deg, #F8FCFF 0%, #FAF8F3 100%)
      `,
    }} className="mc-grain">
      <div style={{ position: "relative", zIndex: 2, maxWidth: 720 }}>
        <p className="mc-eyebrow mc-fade-up-1" style={{ marginBottom: 22 }}>
          MarrsingClause® · Free Clarity Quiz
        </p>
        <h1 className="mc-h1 mc-fade-up-2" style={{
          fontSize: "clamp(40px, 6.8vw, 76px)",
          marginBottom: 22,
        }}>
          Discover where your<br />relationship <em>actually</em> stands.
        </h1>
        <p className="mc-fade-up-3" style={{
          fontSize: 17, color: "var(--mc-text-2)", lineHeight: 1.75,
          maxWidth: 520, margin: "0 auto 38px",
        }}>
          Three minutes. Three pillars. One honest look at the relationship you're building — before, during, or long after the vows.
        </p>

        <div className="mc-fade-up-4" style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 42 }}>
          {["Clarity", "Communication", "Alignment"].map((p, i) => (
            <span key={p} style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11.5, letterSpacing: "0.04em",
              padding: "8px 18px", borderRadius: 999,
              border: `0.5px solid ${i === 0 ? "rgba(111,199,242,0.6)" : "rgba(30,27,22,0.12)"}`,
              color: i === 0 ? "var(--mc-blue-deep)" : "var(--mc-text-3)",
              background: i === 0 ? "rgba(111,199,242,0.14)" : "rgba(255,255,255,0.68)",
            }}>{p}</span>
          ))}
        </div>

        <button onClick={onStart} className="mc-btn-primary mc-fade-up-5" data-testid="quiz-start-cta">
          Start the Free Quiz <ArrowRight size={16} />
        </button>
        <p className="font-mono mc-fade-up-5" style={{
          marginTop: 22, fontSize: 11.5, color: "var(--mc-text-3)",
          letterSpacing: "0.06em",
        }}>
          Free · 3 minutes · No login required · Personalized results
        </p>
      </div>
    </section>
  );
}

function WelcomeNote() {
  return (
    <div style={{ padding: "0 24px", marginTop: -40, position: "relative", zIndex: 3 }}>
      <div style={{
        maxWidth: 640, margin: "0 auto",
        background: "white",
        border: "1px solid var(--mc-border-blue)",
        borderRadius: 22,
        padding: "22px 26px",
        textAlign: "center",
        boxShadow: "0 14px 34px rgba(14,10,31,0.06)",
      }} data-testid="welcome-note">
        <p style={{ color: "var(--mc-text-2)", fontSize: 15.5, lineHeight: 1.75 }}>
          Take a gentle pause. This experience is designed to help you reflect with honesty — not pressure.
        </p>
        <p style={{ marginTop: 8, color: "var(--mc-text-3)", fontSize: 14.5, fontStyle: "italic", fontFamily: "var(--font-serif)" }}>
          There are no right answers. Only awareness.
        </p>
      </div>
      <p className="font-mono" style={{
        textAlign: "center", marginTop: 20,
        fontSize: 11.5, color: "var(--mc-text-3)", letterSpacing: "0.04em",
      }}>
        Private reflection · No judgment · Designed for clarity and intentional connection
      </p>
    </div>
  );
}

/* ────────────────────────────── PILLARS strip ────────────────────────────── */
function Pillars() {
  const items = [
    { name: "Clarity", color: "#4AAED8", bg: "rgba(111,199,242,0.14)",
      body: "Before there is 'us,' there is one mind. Clarity is knowing that mind honestly — your values, your needs, your non-negotiables." },
    { name: "Communication", color: "#317F70", bg: "rgba(127,205,187,0.18)",
      body: "The bridge between what you know about yourself and what your partner can understand and act on." },
    { name: "Alignment", color: "#9A7326", bg: "rgba(215,181,109,0.20)",
      body: "Not compatibility. Are you moving in the same direction, with the same intention, in a way that honours both of you?" },
  ];
  return (
    <section data-testid="pillars-strip" style={{ padding: "80px 24px 32px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <p className="mc-eyebrow">The MarrsingClause® Framework</p>
          <h2 className="mc-h2" style={{ fontSize: "clamp(28px, 3.6vw, 42px)", marginTop: 12 }}>
            Three pillars. <em>One living system.</em>
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }} className="mc-three-col">
          {items.map((p) => (
            <div key={p.name} style={{
              background: "rgba(255,255,255,0.78)",
              border: "1px solid var(--mc-border-blue)",
              borderRadius: 20,
              padding: "28px 26px",
              position: "relative",
              overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: p.color }} />
              <span style={{
                display: "inline-block", padding: "5px 12px",
                background: p.bg, color: p.color,
                borderRadius: 999,
                fontFamily: "var(--font-mono)",
                fontSize: 10.5, letterSpacing: "0.08em",
                textTransform: "uppercase", fontWeight: 500,
                marginBottom: 14,
              }}>{p.name}</span>
              <p style={{ fontSize: 14, color: "var(--mc-text-2)", lineHeight: 1.7 }}>{p.body}</p>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 820px) {
          .mc-three-col { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

/* ────────────────────────────── QUIZ STEP ────────────────────────────── */
function QuizIntro({ onStart }) {
  return (
    <div style={{ padding: "44px 36px 40px" }}>
      <div className="font-mono" style={{
        fontSize: 11, letterSpacing: "0.1em",
        textTransform: "uppercase", color: "var(--mc-text-3)",
        marginBottom: 14,
      }}>Ready when you are</div>
      <h3 className="font-serif" style={{
        fontSize: 26, fontWeight: 400, color: "var(--mc-text)", marginBottom: 14, lineHeight: 1.25,
      }}>
        8 questions. 3 minutes. A clearer view of where you stand.
      </h3>
      <p style={{ fontSize: 14.5, color: "var(--mc-text-3)", lineHeight: 1.7, marginBottom: 28 }}>
        Answer from your own truth — not from what feels expected. Your responses are private and used only to generate your personalized result.
      </p>
      <button onClick={onStart} className="mc-btn-primary" style={{ width: "100%" }} data-testid="quiz-begin-btn">
        Begin Question 1 <ArrowRight size={16} />
      </button>
    </div>
  );
}

function QuizStep({ question, index, total, selected, onSelect, onNext, onBack }) {
  const badgeStyles = {
    Clarity:       { bg: "#EEF8FC", color: "#2C8FB7" },
    Communication: { bg: "#EAF8F5", color: "#317F70" },
    Alignment:     { bg: "#F8EED8", color: "#9A7326" },
  };
  const badge = badgeStyles[question.pillar];

  return (
    <div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "22px 32px 0",
      }}>
        <span className="font-mono" style={{ fontSize: 11.5, color: "var(--mc-text-3)", letterSpacing: "0.06em" }}>
          Question {index + 1} of {total}
        </span>
        <span className="font-mono" data-testid="pillar-badge" style={{
          fontSize: 10.5, padding: "5px 12px", borderRadius: 999,
          background: badge.bg, color: badge.color,
          letterSpacing: "0.06em", fontWeight: 500,
        }}>
          {question.pillar}
        </span>
      </div>

      <div style={{ padding: "26px 36px 34px" }}>
        <p className="font-serif" data-testid="quiz-question" style={{
          fontSize: "clamp(22px, 2.6vw, 28px)",
          fontWeight: 400, lineHeight: 1.3,
          color: "var(--mc-text)", marginBottom: 28,
        }}>{question.q}</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
          {question.opts.map((opt, i) => {
            const isSelected = selected === i;
            return (
              <button
                key={i}
                data-testid={`quiz-opt-${i}`}
                onClick={() => onSelect(i)}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 14,
                  padding: "16px 18px",
                  border: `1px solid ${isSelected ? "var(--mc-blue)" : "var(--mc-border)"}`,
                  background: isSelected ? "var(--mc-blue-pale)" : "white",
                  borderRadius: 14,
                  cursor: "pointer",
                  textAlign: "left",
                  width: "100%",
                  transition: "all 0.18s ease",
                  fontFamily: "var(--font-sans)",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = "rgba(111,199,242,0.65)";
                    e.currentTarget.style.background = "#F3FBFF";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = "var(--mc-border)";
                    e.currentTarget.style.background = "white";
                  }
                }}
              >
                <span style={{
                  width: 28, height: 28, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                  border: `1px solid ${isSelected ? "var(--mc-blue)" : "var(--mc-border)"}`,
                  background: isSelected ? "var(--mc-blue)" : "transparent",
                  color: isSelected ? "white" : "var(--mc-text-3)",
                  fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600,
                }}>{String.fromCharCode(65 + i)}</span>
                <span style={{
                  fontSize: 14.5, lineHeight: 1.55, color: "var(--mc-text)",
                  paddingTop: 4,
                }}>{opt}</span>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {index > 0 ? (
            <button onClick={onBack} data-testid="quiz-back" style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 13, color: "var(--mc-text-3)",
              fontFamily: "var(--font-sans)",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <ArrowLeft size={14} /> Back
            </button>
          ) : <span />}
          <button
            onClick={onNext}
            disabled={selected === undefined}
            className="mc-btn-primary"
            style={{ minHeight: 46, padding: "12px 26px", fontSize: 13.5 }}
            data-testid="quiz-next"
          >
            {index === total - 1 ? "See my results" : "Continue"} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────── PROGRESS ────────────────────────────── */
function Progress({ current, total, stage }) {
  let pct = 0;
  if (stage === "quiz") pct = (current / total) * 100;
  else if (stage === "gate") pct = 100;
  else if (stage === "result") pct = 100;
  return (
    <div style={{ height: 4, background: "var(--mc-border)" }} data-testid="quiz-progress">
      <div style={{
        height: "100%",
        width: `${pct}%`,
        background: stage === "result" ? "var(--mc-teal)" : "var(--mc-blue)",
        transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)",
      }} />
    </div>
  );
}

/* ────────────────────────────── EMAIL GATE ────────────────────────────── */
function EmailGate({ profile, name, setName, email, setEmail, relationshipStage, setRelationshipStage, submitting, onSubmit, onBack }) {
  return (
    <div>
      <div style={{ padding: "22px 36px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="font-mono" style={{ fontSize: 11.5, color: "var(--mc-text-3)", letterSpacing: "0.06em" }}>
          Almost there
        </span>
        <span className="font-mono" style={{
          fontSize: 10.5, padding: "5px 12px", borderRadius: 999,
          background: "#EEF8FC", color: "#2C8FB7", letterSpacing: "0.06em", fontWeight: 500,
        }}>Your Results</span>
      </div>

      <div style={{ padding: "28px 36px 36px" }}>
        <div data-testid="gate-teaser" className="font-serif" style={{
          fontStyle: "italic", fontSize: 16, color: "var(--mc-blue-deep)",
          background: "var(--mc-blue-pale)",
          borderRadius: 14, padding: "16px 20px", marginBottom: 22, lineHeight: 1.6,
        }}>
          Your result is ready. You're a <strong style={{ fontStyle: "normal" }}>{profile.name}</strong> — {profile.teaser}
        </div>
        <p style={{ fontSize: 14.5, color: "var(--mc-text-2)", lineHeight: 1.7, marginBottom: 24 }}>
          Tell us a little about you and we'll send your full personalized breakdown plus a 5-part series tailored to your result.
        </p>

        <div style={{ marginBottom: 14 }}>
          <label className="font-mono" style={{
            fontSize: 11, fontWeight: 500, color: "var(--mc-text-3)",
            display: "block", marginBottom: 6,
            letterSpacing: "0.06em", textTransform: "uppercase",
          }}>First name *</label>
          <input
            data-testid="gate-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your first name"
            required
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label className="font-mono" style={{
            fontSize: 11, fontWeight: 500, color: "var(--mc-text-3)",
            display: "block", marginBottom: 6,
            letterSpacing: "0.06em", textTransform: "uppercase",
          }}>Email address *</label>
          <input
            data-testid="gate-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            required
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label className="font-mono" style={{
            fontSize: 11, fontWeight: 500, color: "var(--mc-text-3)",
            display: "block", marginBottom: 6,
            letterSpacing: "0.06em", textTransform: "uppercase",
          }}>Relationship stage *</label>
          <select
            data-testid="gate-stage"
            value={relationshipStage}
            onChange={(e) => setRelationshipStage(e.target.value)}
            required
            style={{ ...inputStyle, appearance: "auto" }}
          >
            <option value="">Select your stage…</option>
            <option value="Single">Single</option>
            <option value="Dating">Dating</option>
            <option value="In a Relationship">In a Relationship</option>
            <option value="Engaged">Engaged</option>
            <option value="Married">Married</option>
            <option value="It's Complicated">It's Complicated</option>
          </select>
        </div>

        <button
          onClick={onSubmit}
          disabled={submitting || !email || !name || !relationshipStage}
          className="mc-btn-primary"
          data-testid="gate-submit"
          style={{ width: "100%" }}
        >
          {submitting ? "Unlocking…" : "Send me my results"}
          {!submitting && <ArrowRight size={16} />}
        </button>

        <p className="font-mono" style={{
          marginTop: 16, fontSize: 11, color: "var(--mc-text-3)",
          letterSpacing: "0.04em", lineHeight: 1.6,
        }}>
          By submitting you join the MarrsingClause® community and receive a 5-part personalized email series. Unsubscribe any time. We don't share your data.
        </p>

        <button onClick={onBack} style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 12.5, color: "var(--mc-text-3)",
          fontFamily: "var(--font-sans)",
          marginTop: 16, display: "inline-flex", alignItems: "center", gap: 6,
        }}>
          <ArrowLeft size={13} /> Back to questions
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────── RESULT ────────────────────────────── */
function ResultView({ profile, scores }) {
  const colors = { Clarity: "#4AAED8", Communication: "#7FCDBB", Alignment: "#D7B56D" };
  return (
    <div style={{ padding: "32px 36px 40px" }} data-testid="quiz-result">
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          margin: "8px auto 18px",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 28,
          background: `${profile.accent}22`,
        }}>{profile.icon}</div>
        <p className="font-mono" style={{
          fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase",
          color: "var(--mc-text-3)", marginBottom: 8,
        }}>You are a</p>
        <h3 className="font-serif" data-testid="result-name" style={{
          fontSize: 32, fontWeight: 500, color: "var(--mc-text)", marginBottom: 10,
        }}>{profile.name}</h3>
        <p className="font-serif" style={{
          fontStyle: "italic", fontSize: 19, color: profile.accent, marginBottom: 18,
        }}>{profile.title}</p>
        <p style={{
          fontSize: 14.5, color: "var(--mc-text-2)", lineHeight: 1.75,
          maxWidth: 460, margin: "0 auto 28px",
        }}>{profile.body}</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 28 }}>
        {Object.entries(scores).map(([k, v]) => (
          <div key={k} style={{
            background: "var(--mc-light)", borderRadius: 14,
            padding: "16px 12px", textAlign: "center",
          }}>
            <div className="font-mono" style={{
              fontSize: 9.5, letterSpacing: "0.08em", textTransform: "uppercase",
              color: "var(--mc-text-3)", marginBottom: 8,
            }}>{k}</div>
            <div style={{
              height: 5, background: "var(--mc-border)", borderRadius: 999,
              overflow: "hidden", margin: "0 auto 8px", maxWidth: 70,
            }}>
              <div style={{ width: `${v}%`, height: "100%", background: colors[k], borderRadius: 999 }} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mc-text)" }}>{v}%</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <a href={AMAZON_URL} target="_blank" rel="noreferrer" className="mc-btn-primary" data-testid="result-book-cta" style={{ width: "100%" }}>
          Get The Power of Marrsing — the book that started it all <ExternalLink size={14} />
        </a>
        <Link to="/#waitlist" className="mc-btn-secondary" data-testid="result-waitlist-cta" style={{ width: "100%" }}>
          Join the MarrsingClause® app waitlist <ArrowRight size={14} />
        </Link>
        <a href={APP_URL} target="_blank" rel="noreferrer" data-testid="result-app-cta" style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
          textDecoration: "none", color: "var(--mc-text-3)", fontSize: 12.5, marginTop: 6,
          fontFamily: "var(--font-mono)", letterSpacing: "0.05em",
        }}>
          Visit app.marrsinglife.com <ExternalLink size={11} />
        </a>
      </div>

      <p className="font-mono" style={{
        textAlign: "center", marginTop: 22, fontSize: 11.5,
        color: "var(--mc-text-3)", letterSpacing: "0.04em",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%",
      }}>
        <Mail size={12} /> Full results breakdown is on its way to your inbox.
      </p>
    </div>
  );
}

/* ────────────────────────────── SOCIAL PROOF ────────────────────────────── */
function SocialProof() {
  const ts = [
    { quote: "Taking the Clarity Quiz showed me things about myself I'd been avoiding for years. Not bad things — just true things. That clarity changed the way I show up in my relationship.", author: "Beta user · Married 6 years" },
    { quote: "We had the most important pre-wedding conversation of our entire relationship because of this quiz. I genuinely wish someone had handed it to us sooner.", author: "Engaged · Planning a June wedding" },
    { quote: "I finally feel like we're building something — not just surviving each other. Three minutes changed the conversation we'd been avoiding for two years.", author: "Newlywed · 8 months married" },
  ];
  return (
    <section style={{ padding: "88px 24px", background: "white" }} data-testid="quiz-social-proof">
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <p className="mc-eyebrow">From the community</p>
          <h2 className="mc-h2" style={{ fontSize: "clamp(28px, 3.6vw, 42px)", marginTop: 12 }}>
            What couples are <em>saying.</em>
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }} className="mc-three-col">
          {ts.map((t, i) => (
            <div key={i} style={{
              background: "var(--mc-cream)",
              border: "1px solid var(--mc-border-blue)",
              borderRadius: 22, padding: 28,
            }}>
              <div style={{ display: "flex", gap: 3, marginBottom: 14 }}>
                {[0,1,2,3,4].map((s) => (
                  <span key={s} style={{
                    width: 14, height: 14,
                    background: "var(--mc-gold)",
                    clipPath: "polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)",
                  }} />
                ))}
              </div>
              <p className="font-serif" style={{
                fontStyle: "italic", fontSize: 16, color: "var(--mc-text)",
                lineHeight: 1.65, marginBottom: 16,
              }}>"{t.quote}"</p>
              <p className="font-mono" style={{
                fontSize: 11, color: "var(--mc-text-3)", letterSpacing: "0.04em",
              }}>{t.author}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const inputStyle = {
  width: "100%",
  padding: "14px 18px",
  borderRadius: 12,
  border: "1px solid var(--mc-border)",
  background: "white",
  fontFamily: "var(--font-sans)",
  fontSize: 15,
  color: "var(--mc-text)",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  minHeight: 50,
};
